
import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Lightbulb, Link, FileText, Code } from 'lucide-react';
import { ImageUpload } from "@/components/ImageUpload";

interface ContentAssistantTabProps {
  name: string;
  setName: (name: string) => void;
  generalInfo: string;
  setGeneralInfo: (info: string) => void;
  imageUrl: string;
  setImageUrl: (url: string) => void;
}

const ContentAssistantTab = ({
  name,
  setName,
  generalInfo,
  setGeneralInfo,
  imageUrl,
  setImageUrl
}: ContentAssistantTabProps) => {
  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="name" className="text-base font-medium">Solution Name *</Label>
        <Input 
          id="name" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="Enter the AI solution name (e.g., ChatGPT, LayerFlow, DALL-E)"
          className="mt-2"
          required
        />
      </div>
      
      <div>
        <Label htmlFor="image" className="text-base font-medium">Solution Image (Optional)</Label>
        <ImageUpload
          value={imageUrl}
          onChange={setImageUrl}
          placeholder="Enter image URL or upload from device"
          className="mt-2"
        />
        <p className="text-sm text-muted-foreground mt-1">
          Add a representative image for this AI solution
        </p>
      </div>
      
      <div>
        <Label htmlFor="generalInfo" className="text-base font-medium">General Info for AI Research (Optional)</Label>
        <Textarea 
          id="generalInfo" 
          value={generalInfo} 
          onChange={(e) => setGeneralInfo(e.target.value)} 
          placeholder="Provide detailed information about the AI solution. Include URLs, descriptions, features, and any relevant context..."
          className="mt-2 min-h-[200px]"
          required
        />
        
        <div className="mt-3 space-y-3">
          <p className="text-sm text-muted-foreground">
            <strong>The AI will research and generate comprehensive content based on your input.</strong>
          </p>
          
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-blue-600" />
                Tips for Best Results
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Link className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Include URLs:</strong> ArXiv papers, GitHub repos, official websites, documentation
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <FileText className="h-3 w-3 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Describe Purpose:</strong> What the solution does, its main features and capabilities
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Code className="h-3 w-3 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Technical Details:</strong> Architecture, models used, performance metrics
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-3 w-3 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Context:</strong> Target audience, use cases, industry applications
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="text-xs text-muted-foreground space-y-1">
            <p><strong>Example URLs that work well:</strong></p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>https://arxiv.org/abs/2506.04228 (Research papers)</li>
              <li>https://github.com/username/repo (Open source projects)</li>
              <li>https://docs.example.com (Official documentation)</li>
              <li>https://platform.openai.com (Official websites)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentAssistantTab;
